import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { films, filmRegions, filmTypes } from "@/mocks/films";
import { useFavorites } from "@/hooks/useFavorites";
import FilmGrid from "@/components/feature/FilmGrid";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { typeLabel } from "@/utils/film";

const PAGE_SIZE = 8;
const ALL = "全部";
const VALID_TYPES = ["all", ...filmTypes.map((t) => t.key)];

const decadeOptions = [ALL, "2024", "2023", "2022", "2021"];

const sortOptions = [
  { key: "updated", label: "更新时间" },
  { key: "popular", label: "热门程度" },
  { key: "rating", label: "评分" },
];

const categoryIntro: Record<string, string> = {
  movie: "精选伦理题材剧情长片，深入探讨家庭、婚姻与人性的复杂议题。",
  tv: "连续剧集细腻刻画亲密关系、家庭伦理与都市情感，陪伴你的每一段闲暇时光。",
  short: "以精炼篇幅直击主题的伦理短片，短小却余韵悠长。",
  all: "汇集平台全部伦理影视作品，电影、电视剧与短片一应俱全。",
};

export default function Category() {
  const { type = "movie" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { favorites, toggleFavorite } = useFavorites();

  const currentType = VALID_TYPES.includes(type) ? type : "movie";

  const region = searchParams.get("region") ?? ALL;
  const decade = searchParams.get("decade") ?? ALL;
  const sort = searchParams.get("sort") ?? "updated";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1"));

  const [keywordFallback, setKeywordFallback] = useState(false);

  useEffect(() => {
    setKeywordFallback(false);
  }, [currentType]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL || (key === "sort" && value === "updated")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    setSearchParams(next);
    setKeywordFallback(false);
  };

  const filtered = useMemo(() => {
    let list = films.filter((f) => currentType === "all" || f.type === currentType);
    if (region !== ALL) list = list.filter((f) => f.region === region);
    if (decade !== ALL) list = list.filter((f) => String(f.year) === decade);

    return [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popular") return b.popularity - a.popularity;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [currentType, region, decade, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasActiveFilter = region !== ALL || decade !== ALL;

  const title = currentType === "all" ? "全部作品" : `伦理${typeLabel(currentType)}`;
  const crumbLabel = currentType === "all" ? "全部作品" : typeLabel(currentType);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: crumbLabel }]} />

      <header className="mt-5">
        <h1 className="font-heading text-3xl font-bold text-foreground-950">{title}</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {categoryIntro[currentType]}
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={resetFilters}
          className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
            currentType === "all"
              ? "bg-accent-500 font-medium text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-accent-50 hover:text-accent-600"
          }`}
        >
          全部
        </button>
        {filmTypes.map((t) => (
          <Link
            key={t.key}
            to={`/category/${t.key}`}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
              currentType === t.key
                ? "bg-primary-500 font-medium text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
            }`}
          >
            {t.label}
          </Link>
        ))}
      </div>

      <div className="mt-5">
        <FilterBar
          groups={[
            { key: "region", label: "地区", options: [ALL, ...filmRegions] },
            { key: "decade", label: "年代", options: decadeOptions },
          ]}
          values={{ region, decade }}
          onChange={updateParam}
          onReset={resetFilters}
          hasActiveFilter={hasActiveFilter}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-3">
        <p className="text-sm text-secondary-600">
          共找到 <strong className="text-primary-700">{filtered.length}</strong> 部作品
        </p>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-sm text-secondary-500">
            <i className="ri-sort-desc"></i>
            排序
          </span>
          <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => updateParam("sort", opt.key)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  sort === opt.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6">
            <FilmGrid
              films={pageItems}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
            />
          </div>
          <Pagination
            current={safePage}
            total={totalPages}
            onChange={(p) => updateParam("page", String(p))}
          />
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-secondary-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            没有找到匹配的作品
          </h3>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            试着调整筛选条件，或浏览平台的热门推荐内容。
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                resetFilters();
                setKeywordFallback(true);
              }}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              清空筛选条件
            </button>
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              返回首页
            </Link>
          </div>
          {keywordFallback && (
            <p className="mt-4 text-xs text-secondary-500">已重置筛选，正在显示全部作品</p>
          )}
        </div>
      )}
    </div>
  );
}